import { PatientLayout } from "@/components/layout/patient-layout";
import { useState } from "react";
import { Dumbbell, ChevronDown, ChevronUp, AlertTriangle, Info, CheckCircle2, Printer } from "lucide-react";

interface Exercise {
  name: string;
  description: string;
  reps: string;
  frequency: string;
  warning?: string;
}

interface ExerciseLeaflet {
  id: string;
  condition: string;
  shortName: string;
  intro: string;
  exercises: Exercise[];
  tips: string[];
  stopIfMessage: string;
}

const LEAFLETS: ExerciseLeaflet[] = [
  {
    id: "axspa",
    condition: "Axial Spondyloarthritis (axSpA / Ankylosing Spondylitis)",
    shortName: "axSpA",
    intro:
      "Exercise is one of the most effective things you can do to manage axSpA. Moving gently every day helps reduce pain and morning stiffness. These exercises take about 20–30 minutes and are most helpful done daily.",
    exercises: [
      {
        name: "Chin Tuck",
        description:
          "Sit or stand tall. Gently draw your chin straight back as if making a 'double chin'. Hold for 3–5 seconds, then relax. This helps keep your neck moving freely.",
        reps: "10 repetitions",
        frequency: "2–3 times daily",
      },
      {
        name: "Thoracic Extension over Chair",
        description:
          "Sit in a firm chair and put your hands gently behind your head. Lean back carefully over the top edge of the chair to open up your upper back. Hold for 5 seconds, then sit up slowly.",
        reps: "5–8 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Hip Flexor Stretch",
        description:
          "Kneel on one knee (use a cushion for comfort). Slowly push your hips forward until you feel a gentle stretch at the front of the kneeling leg. Hold for 20–30 seconds. Repeat on the other side.",
        reps: "3 holds each side",
        frequency: "Once daily",
      },
      {
        name: "Deep Breathing",
        description:
          "Sit up tall and breathe in slowly through your nose, letting your chest expand as wide as it can. Hold for 2 seconds, then breathe out slowly. This helps keep your chest moving well.",
        reps: "5–10 breaths",
        frequency: "2 times daily",
      },
      {
        name: "Prone Lying",
        description:
          "Lie flat on your front on a firm surface or your bed. Relax completely and let your back flatten out. This gentle position helps prevent your spine from rounding forward.",
        reps: "20–30 minutes",
        frequency: "Once daily (e.g. after breakfast)",
      },
    ],
    tips: [
      "Morning stiffness is very common with axSpA — gentle exercises in bed before you get up can really help.",
      "A warm shower before exercising may ease your stiffness.",
      "Swimming is an excellent, low-impact activity for axSpA.",
      "Try to sit and stand as tall and upright as you can during the day.",
    ],
    stopIfMessage:
      "Stop and contact your physiotherapist or rheumatology team if you develop new or worsening pain in your arms or legs, or any numbness or tingling.",
  },
  {
    id: "ra",
    condition: "Rheumatoid Arthritis (RA)",
    shortName: "RA",
    intro:
      "Gentle exercise helps keep your joints moving and your muscles strong. It is safe to exercise with RA — in fact it is one of the best things you can do for your health. Listen to your body and rest during bad flares, but try to keep moving gently.",
    exercises: [
      {
        name: "Finger Flexion & Extension",
        description:
          "Gently make a soft fist, then open your hand and spread your fingers as wide as feels comfortable. Do not push into pain — the movement should feel gentle.",
        reps: "10 repetitions, each hand",
        frequency: "2–3 times daily",
      },
      {
        name: "Wrist Circles",
        description:
          "Rest your elbow on a table. Slowly rotate your wrist in a circle — 5 times one way, then 5 times the other way. Keep the movement slow and gentle.",
        reps: "5 circles each direction",
        frequency: "2 times daily",
      },
      {
        name: "Seated Knee Straightening",
        description:
          "Sit in a chair. Slowly straighten one knee, hold your leg straight for 5 seconds, then lower it slowly. Swap legs.",
        reps: "10 repetitions each leg",
        frequency: "Once daily",
      },
      {
        name: "Ankle Pumps",
        description:
          "Sitting or lying down, flex your foot up and down, then gently circle it. This gets your blood moving and reduces stiffness.",
        reps: "10 pumps + 5 circles, each foot",
        frequency: "Several times daily",
      },
      {
        name: "Gentle Walking",
        description:
          "A 10–20 minute walk at a comfortable pace. Build up gradually over time. Wear supportive shoes.",
        reps: "10–20 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "Avoid high-impact activities like running or jumping during a flare.",
      "Warm up gently before any exercise and cool down afterwards.",
      "Warm water exercise (hydrotherapy) can be very helpful for RA.",
      "If a joint feels hot and swollen, rest it — but try to keep other joints moving gently.",
    ],
    stopIfMessage:
      "Stop any exercise that causes significant joint pain, swelling, or heat. Contact your rheumatology team if a joint is noticeably more swollen or painful than usual.",
  },
  {
    id: "oa",
    condition: "Osteoarthritis (OA) — Knee & Hip",
    shortName: "OA (Knee & Hip)",
    intro:
      "Exercise is proven to reduce pain and improve how well you can move with osteoarthritis. Strengthening the muscles around your joints takes pressure off the joint itself. Start gently and build up gradually — some mild muscle ache after exercise is normal.",
    exercises: [
      {
        name: "Seated Leg Raises",
        description:
          "Sit in a firm chair. Straighten one knee and lift your leg so it is level with your hip. Hold for 5 seconds, lower slowly. Repeat with the other leg.",
        reps: "10–15 repetitions each leg",
        frequency: "Once daily",
      },
      {
        name: "Standing Hip Abduction",
        description:
          "Stand holding a wall or chair for balance. Slowly lift one leg out to the side, keeping your knee and foot pointing forward. Hold for 2 seconds, lower slowly.",
        reps: "10 repetitions each leg",
        frequency: "Once daily",
        warning: "Only raise as high as is comfortable — do not lean your body to the side.",
      },
      {
        name: "Mini Squats",
        description:
          "Stand with feet shoulder-width apart, holding a surface for balance. Bend your knees slightly (just a small bend) as if starting to sit, then stand back up.",
        reps: "10 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Calf Raises",
        description:
          "Stand holding a surface. Rise slowly onto your tiptoes, then lower back down. This strengthens your calves and improves balance.",
        reps: "10–15 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Walking",
        description:
          "Regular walking on flat ground in supportive shoes. Start with 10 minutes and build up to 30 minutes most days.",
        reps: "10–30 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "Even a small amount of weight loss significantly reduces the load on your knee and hip joints.",
      "Cycling (stationary or regular) is excellent for OA as it is low impact.",
      "Pace yourself — joint pain lasting more than 2 hours after exercise means you overdid it.",
      "Use a walking aid if your physiotherapist or doctor has recommended one.",
    ],
    stopIfMessage:
      "Stop if you experience sharp joint pain, a feeling of your joint giving way, or significant swelling. Contact your physiotherapist if these occur.",
  },
  {
    id: "gout",
    condition: "Gout",
    shortName: "Gout",
    intro:
      "During a gout attack, rest the affected joint and raise it up if you can. Once the flare has completely settled, gentle exercise and a healthy lifestyle can help prevent future attacks.",
    exercises: [
      {
        name: "Ankle Range of Motion (between attacks only)",
        description:
          "Sitting down, gently rotate your ankle in slow circles and flex your foot up and down. This helps keep the joint mobile between flares.",
        reps: "10 circles each direction",
        frequency: "Once daily — only when completely pain-free",
      },
      {
        name: "Toe Curls (between attacks only)",
        description:
          "Sitting down, curl your toes downwards and hold for 5 seconds, then spread them wide. This keeps toe joint mobility.",
        reps: "10 repetitions",
        frequency: "Once daily — only when completely pain-free",
      },
      {
        name: "Walking (between attacks)",
        description:
          "Regular low-impact walking in supportive, well-fitting shoes. Start gently and build up gradually.",
        reps: "15–30 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "During an attack: rest, raise the joint, use an ice pack wrapped in a cloth for up to 20 minutes, and take your prescribed medication.",
      "Stay well hydrated — aim for at least 2 litres of water each day.",
      "Cut down on alcohol (especially beer and spirits) and foods high in purines (red meat, shellfish, offal).",
      "Maintaining a healthy weight helps reduce uric acid levels.",
    ],
    stopIfMessage:
      "Do not exercise the affected joint during an active gout attack. Wait until the inflammation has completely gone before resuming exercises.",
  },
  {
    id: "sle",
    condition: "Systemic Lupus Erythematosus (SLE / Lupus)",
    shortName: "Lupus",
    intro:
      "Exercise is safe and beneficial with lupus, but needs to be adapted to how you are feeling. Managing your energy levels is key — the principle is to stop before you feel exhausted rather than pushing through. Start gently and increase gradually.",
    exercises: [
      {
        name: "Gentle Walking",
        description:
          "Start with 5–10 minutes at a comfortable pace. Only increase when you feel ready. Try walking in the early morning or evening to avoid strong sunlight.",
        reps: "5–15 minutes",
        frequency: "3 times a week to start",
      },
      {
        name: "Seated Arm Raises",
        description:
          "Sit in a chair. Slowly raise both arms out to the side to shoulder height and lower them again. Keep the movement slow and controlled.",
        reps: "10 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Ankle Pumps & Circles",
        description:
          "Sitting or lying, flex your feet up and down and circle them gently. This helps circulation and keeps joints mobile on days when you have less energy.",
        reps: "10 pumps + 5 circles each foot",
        frequency: "Several times daily",
      },
      {
        name: "Gentle Stretching",
        description:
          "Slow, gentle stretches for your arms, legs, and back. Hold each stretch for 15–20 seconds. Stretch only to the point of mild tension, not pain.",
        reps: "2–3 stretches per area",
        frequency: "Once daily",
      },
    ],
    tips: [
      "Use the 'stop before you drop' approach — always leave some energy in reserve.",
      "Keep an activity diary to spot patterns in your energy levels.",
      "If you feel significantly more tired than usual for 24–48 hours after exercise, you have done too much — reduce the amount next time.",
      "Wear sun-protective clothing and use SPF 50+ sunscreen if exercising outdoors.",
    ],
    stopIfMessage:
      "Stop if exercise causes a significant increase in fatigue, joint pain, or any new symptoms. If you are in a flare, stick to very gentle movement only, and check with your rheumatology team.",
  },
];

function LeafletCard({ leaflet }: { leaflet: ExerciseLeaflet }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-rose-50/50 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          <div className="bg-rose-100 rounded-xl p-2.5 shrink-0">
            <Dumbbell className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{leaflet.condition}</p>
            <p className="text-sm text-slate-500 mt-0.5">{leaflet.exercises.length} exercises included</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 ml-3" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 ml-3" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-rose-100 px-6 pb-6 pt-5 space-y-6">
          <p className="text-sm text-slate-700 leading-relaxed">{leaflet.intro}</p>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Your Exercises</h3>
            {leaflet.exercises.map((ex, i) => (
              <div key={i} className="bg-rose-50 border border-rose-100 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-slate-900 text-sm">{ex.name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs bg-rose-200 text-rose-800 rounded-full px-2.5 py-0.5 font-medium">{ex.reps}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5">{ex.frequency}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{ex.description}</p>
                {ex.warning && (
                  <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{ex.warning}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {leaflet.tips.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-900 mb-1">
                <Info className="h-4 w-4" />
                Helpful tips
              </div>
              <ul className="space-y-1.5">
                {leaflet.tips.map((tip, i) => (
                  <li key={i} className="text-sm text-blue-800 flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-start gap-2 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
              <p><strong>When to stop:</strong> {leaflet.stopIfMessage}</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg px-4 py-2 transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print this leaflet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PatientExercises() {
  return (
    <PatientLayout title="Exercise Guides">
      <div className="max-w-2xl space-y-6">
        <p className="text-lg text-slate-600 leading-relaxed -mt-4">
          Gentle, condition-specific exercise guides prepared by rheumatology physiotherapists. Regular movement is one of the most effective ways to manage rheumatic conditions.
        </p>

        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 flex items-start gap-3 text-sm text-amber-800">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <p>
            These guides are a general starting point. Always follow advice from your own physiotherapist or healthcare team, as they know your individual situation. If you are unsure whether an exercise is right for you, ask before starting.
          </p>
        </div>

        <div className="space-y-4">
          {LEAFLETS.map((leaflet) => (
            <LeafletCard key={leaflet.id} leaflet={leaflet} />
          ))}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Remember:</strong> Some muscle ache after exercise is normal, especially when you are just starting out. Joint pain that lasts more than 2 hours after exercise, or any new swelling, means you should reduce the amount next time. If in doubt, contact your physiotherapist or rheumatology team.
          </p>
        </div>
      </div>
    </PatientLayout>
  );
}
